<template>
    <div>
        <div
            :class="[
                hasErrorStartingWith(errors, 'email')
                    ? 'border-red-600'
                    : 'border-gray-200',
                'mt-4 mb-6 lg:mb-4 bg-white shadow rounded-lg border',
            ]"
        >
            <div
                :class="[
                    hasErrorStartingWith(errors, 'email')
                        ? 'bg-red-500 text-white border-red-600'
                        : 'bg-gray-100 text-gray-800 border-gray-200',
                    'flex items-center justify-between px-4 py-5 sm:px-6 rounded-t-lg border-b',
                ]"
            >
                <div class="flex items-center">
                    <UserIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                    <h2 class="font-bold">Contact Information</h2>
                </div>
            </div>

            <div class="md:grid md:grid-cols-2 md:gap-6 px-4 py-5 sm:px-6">
                <div class="mt-0 md:col-span-2">
                    <div class="grid grid-cols-6 gap-6">
                        <div class="col-span-6 sm:col-span-3">
                            <label
                                for="email"
                                class="block text-sm font-medium text-gray-700"
                            >
                                Email
                            </label>

                            <div class="mt-1 flex rounded-md shadow-sm">
                                <div
                                    class="relative flex items-stretch grow focus-within:z-10"
                                >
                                    <input
                                        v-model="inputs.email"
                                        :ref="refs.email"
                                        @blur="onBlur"
                                        name="email"
                                        id="email"
                                        type="email"
                                        autocomplete="email"
                                        placeholder="(optional)"
                                        :class="[
                                            sentLoginLink
                                                ? 'ring-1 ring-nutreance-green border-nutreance-green rounded-none rounded-l-md'
                                                : 'rounded-md',
                                            hasError(errors, 'email')
                                                ? 'border-red-300 text-red-900 placeholder-red-300 focus:ring-red-500 focus:border-red-500'
                                                : 'focus:ring-nutreance-green focus:border-nutreance-green',
                                            'appearance-none block w-full pl-3 pr-10 py-2 border border-gray-300 placeholder-gray-400 focus:outline-none sm:text-sm',
                                        ]"
                                        :aria-invalid="
                                            hasError(errors, 'email')
                                                ? 'true'
                                                : 'false'
                                        "
                                        :aria-describedby="
                                            hasError(errors, 'email')
                                                ? 'email-error'
                                                : ''
                                        "
                                    />
                                    <div
                                        v-if="hasError(errors, 'email')"
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <InputTooltip
                                        v-else
                                        message="Recommended if you want to track your order or save your payment method"
                                    />
                                </div>
                                <button
                                    v-if="sentLoginLink"
                                    @click="open = true"
                                    type="button"
                                    class="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-nutreance-green text-sm font-medium rounded-r-md bg-nutreance-green hover:bg-nutreance-green-darker focus:outline-none ring-1 ring-nutreance-green"
                                >
                                    <LoginIcon
                                        class="h-5 w-5 text-white animate-bounce"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                            <p
                                v-if="hasError(errors, 'email')"
                                class="mt-2 text-sm text-red-600"
                                id="email-error"
                            >
                                {{ error(errors, "email") }}
                            </p>
                        </div>

                        <div class="col-span-6">
                            <div class="relative flex items-start">
                                <div class="flex items-center h-5">
                                    <input
                                        v-model="inputs.email_opt_in"
                                        id="email_opt_in"
                                        name="email_opt_in"
                                        type="checkbox"
                                        class="focus:ring-transparent h-4 w-4 text-nutreance-green border-gray-300 rounded"
                                    />
                                </div>
                                <div class="ml-3 text-sm">
                                    <label
                                        for="email_opt_in"
                                        class="text-gray-900"
                                        >I would like to receive emails about
                                        coupons, discounts, and related
                                        products.</label
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-emerald-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <ShoppingCartIcon
                                        class="h-6 w-6 text-nutreance-green"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Hey, we remember you!
                                    </DialogTitle>
                                </div>
                            </div>
                            <div class="mt-6 mb-4 mx-5 sm:mx-4 space-y-4">
                                <svg
                                    class="mt-4 mb-8 mx-auto checkmark"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 52 52"
                                    aria-hidden="true"
                                >
                                    <circle
                                        class="checkmark-circle"
                                        cx="26"
                                        cy="26"
                                        r="25"
                                        fill="none"
                                    />
                                    <path
                                        class="checkmark-check"
                                        fill="none"
                                        d="M14.1 27.2l7.1 7.2 16.7-16.8"
                                    />
                                </svg>

                                <p class="text-gray-500">
                                    For a quicker checkout, we just sent you an
                                    email with a
                                    <span
                                        class="text-nutreance-green font-semibold"
                                        >one-time login link</span
                                    >. Simply click this link to securely check
                                    out with your saved details.
                                </p>

                                <p class="text-gray-500">
                                    Or, if you would prefer to fill out your
                                    information again, you may ignore the email
                                    and proceed as a guest.
                                </p>
                            </div>

                            <div
                                class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                            >
                                <button
                                    @click="open = false"
                                    name="yes"
                                    type="button"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green text-base font-medium text-white hover:bg-nutreance-green-darker focus:outline-none sm:col-start-2 sm:text-sm"
                                >
                                    <CheckIcon
                                        class="h-5 w-5 mr-2"
                                        aria-hidden="true"
                                    />
                                    Ok
                                </button>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, watch } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { LoginIcon } from "@heroicons/vue/outline";
import {
    CheckIcon,
    ExclamationCircleIcon,
    ShoppingCartIcon,
    UserIcon,
    XIcon,
} from "@heroicons/vue/solid";
import { useForm } from "../../functions/useForm";
import InputTooltip from "../InputTooltip.vue";
import { sendLoginLink } from "../../api/auth";
import { rememberAbandonedCart } from "../../api/cart";

const props = defineProps<{
    errors?: any;
    old?: any;
}>();

const { error, hasError, hasErrorStartingWith, oldInput } = useForm();
const sentLoginLink = ref(false);
const open = ref(false);

const refs = {
    email: ref<any>(null),
};

const inputs = reactive({
    email: oldInput(props.old, "email"),
    email_opt_in: !!(Object.prototype.hasOwnProperty.call(
        props.old,
        "email_opt_in",
    )
        ? oldInput(props.old, "email_opt_in")
        : true),
});

watch(
    () => inputs.email,
    () => (sentLoginLink.value = false),
);

function onBlur() {
    if (inputs.email && !sentLoginLink.value) {
        sendLoginLink({
            email: inputs.email,
        })
            .then((data) => {
                if (data.sent) {
                    sentLoginLink.value = true;
                    open.value = true;
                }
            })
            .finally(() => {
                rememberAbandonedCart({
                    email: inputs.email,
                });
            });
    }
}

onMounted(() => refs.email.value.focus());
</script>
